import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgProgress } from '@ngx-progressbar/core';
import { ToastrService } from 'ngx-toastr';
import { APIOperableComponent } from '../../../../shared/components/api-operable/api-operable.component';
import { ConfirmationComponent } from '../../../../shared/components/confirmation/confirmation.component';
import { ConfirmationModel } from '../../../../shared/model/confirmation.model';
import { ExportToExcelService } from '../../../../shared/services/export-to-excel.service';
import { UIDialogService } from '../../../../shared/services/ui-dialog.service';
import { AuthService } from '../../../security/service/auth.service';
import { PartsSearchModel } from '../../model/parts-search.model';
import { PartsUIMessages } from '../../model/parts-ui.message';
import { PartsService } from '../../service/parts.service';

@Component({
  selector: 'app-parts-list',
  templateUrl:
    'console/app-js/modules/parts/components/list/parts-list.component.html'
})
export class PartsListComponent extends APIOperableComponent implements OnInit {
  @ViewChild('statusCombobox', { static: true }) statusCombobox;
  private searchModel = new PartsSearchModel();
  public opened = false;
  public selectedRows = [];
  private productLines = [];
  public productLinesFiltered = [];
  public selectedProductLine: any;
  private makes = [];
  public makesFiltered = [];
  public selectedMake: any;
  private partTypes = [];
  public partTypesFiltered = [];
  public selectedPartType: any;
  public loading = false;
  public fullscreen = false;
  public noSearchResults = false;
  public statuses = [
    { id: 'all', name: 'All' },
    { id: 'true', name: 'Active' },
    { id: 'false', name: 'Inactive' }
  ];
  public selectedStatus: any;
  private EXCEL_FILE_NAME = 'PartsSearchResult.xlsx';
  public canManageParts = false;
  public noCost: string = '0';

  constructor(
    private exportToExcelService: ExportToExcelService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected progressService: NgProgress,
    private dialogService: UIDialogService,
    protected toastr: ToastrService,
    protected authService: AuthService,
    protected partsService: PartsService
  ) {
    super(progressService, toastr, authService);
  }

  ngOnInit() {
    // VL-2482: Admin Portal part create/edit/activate/retire is disabled.
    this.canManageParts = false;
    this.searchModel = this.partsService.getPartsSearchInput();
    this.getProductLines();
    this.getMakes();
    this.getPartTypes();
    this.getStatusTypes();

    // search on init if there were results from same session.
    // searching again in case there were updates to a part since last view
    if (this.searchModel.results.length > 0) {
      this.onSearch();
    }
  }

  public onSearch(): void {
    this.searchModel.results = [];
    this.selectedRows = [];
    this.partsService.setPartsSearchInput(this.searchModel);
    this.searchParts(1);
  }

  public onReset() {
    this.searchModel = new PartsSearchModel();
    this.selectedMake = null;
    this.selectedPartType = null;
    this.selectedProductLine = null;
    this.selectedStatus = this.statuses[1];
    this.searchModel.filterModel.active = this.statuses[1].name;
    this.noSearchResults = false;
    this.partsService.setPartsSearchInput(new PartsSearchModel());
  }

  private searchParts(pageNumber: number) {
    this.progressRef.start();
    this.loading = true;
    this.searchModel.pageNumber = pageNumber;
    this.partsService.searchParts(this.searchModel).subscribe(
      res => {
        if (res.responseCode === 200 && res.results) {
          this.searchModel.count = res.rows;
          this.searchModel.pageNumber = res.pageNumber;
          this.searchModel.totalCount = res.count;
          this.searchModel.totalPages = res.totalPages;
          res.results.map(part => {
            part.isActive = part.isActive ? 'Active' : 'Inactive';
            part.isActionProduct = part.isActionProduct ? 'Yes' : 'No';
            part.skuAsHtml = this.convertToHtmlContent(part.sku);
            part.materialCostMinimum = part.materialCostMinimum
              ? parseFloat(part.materialCostMinimum).toFixed(2)
              : parseFloat(this.noCost).toFixed(2);
            part.materialCostMaximum = part.materialCostMaximum
              ? parseFloat(part.materialCostMaximum).toFixed(2)
              : parseFloat(this.noCost).toFixed(2);
          });
          this.searchModel.results.push(...res.results);
          this.noSearchResults = res.results.length === 0 ? true : false;
        } else {
          this.noSearchResults = true;
        }
        this.partsService.setPartsSearchInput(this.searchModel);
      },
      () => {
        this.onAPIError(PartsUIMessages.SEARCH_PARTS_ERROR);
        this.searchModel.results = [];
        this.partsService.setPartsSearchInput(this.searchModel);
      },
      () => {
        this.loading = false;
        this.progressRef.complete();
      }
    );
  }

  public infinityScroll(): void {
    if (this.searchModel.pageNumber < this.searchModel.totalPages) {
      this.searchParts((this.searchModel.pageNumber += 1));
    }
  }

  public onFullscreenMode(): void {
    this.fullscreen = !this.fullscreen;
  }

  public getProductLines(): void {
    this.partsService.getProductLines().subscribe(data => {
      this.productLines = data;
      this.productLinesFiltered = data;
      // populate the drop down if it was part of the last search session
      if (
        this.searchModel.filterModel.productLine !== null &&
        this.searchModel.filterModel.productLine !== ''
      ) {
        const filteredItem = this.productLines.filter(
          s => s.name === this.searchModel.filterModel.productLine
        );
        this.selectedProductLine =
          filteredItem[0] == undefined
            ? { name: this.searchModel.filterModel.productLine, id: null }
            : filteredItem[0];
      }
    });
  }

  public getMakes(): void {
    this.partsService.getMakes().subscribe(data => {
      this.makes = data;
      this.makesFiltered = data;
      // populate the drop down if it was part of the last search session
      if (
        this.searchModel.filterModel.make !== null &&
        this.searchModel.filterModel.make !== ''
      ) {
        const filteredItem = this.makes.filter(
          s => s.name === this.searchModel.filterModel.make
        );
        this.selectedMake =
          filteredItem[0] == undefined
            ? { name: this.searchModel.filterModel.make, id: null }
            : filteredItem[0];
      }
    });
  }

  public getPartTypes(): void {
    this.partsService.getPartTypes().subscribe(data => {
      this.partTypes = data;
      this.partTypesFiltered = data;
      // populate the drop down if it was part of the last search session
      if (
        this.searchModel.filterModel.partType !== null &&
        this.searchModel.filterModel.partType !== ''
      ) {
        const filteredItem = this.partTypes.filter(
          s => s.name === this.searchModel.filterModel.partType
        );
        this.selectedPartType =
          filteredItem[0] == undefined
            ? { name: this.searchModel.filterModel.partType, id: null }
            : filteredItem[0];
      }
    });
  }

  public getStatusTypes(): void {
    // populate the drop down if it was part of the last search session
    if (this.searchModel.filterModel.active === 'Active') {
      this.selectedStatus = this.statuses[1];
    } else if (this.searchModel.filterModel.active === 'Inactive') {
      this.selectedStatus = this.statuses[2];
    } else {
      this.selectedStatus = this.statuses[0];
      this.searchModel.filterModel.active = this.selectedStatus.name;
    }
  }

  public onProductLineValueChange(item: any) {
    if (item) {
      this.searchModel.filterModel.productLine = item['name'];
    } else {
      this.searchModel.filterModel.productLine = null;
      this.selectedProductLine = null;
    }
  }

  public onProductLineFilterChange(value: any) {
    this.productLinesFiltered = this.productLines.filter(
      productLine =>
        productLine.name.toLowerCase().indexOf(value.toLowerCase()) !== -1
    );
  }

  public onMakeValueChange(item: any) {
    if (item) {
      this.searchModel.filterModel.make = item['name'];
    } else {
      this.searchModel.filterModel.make = null;
      this.selectedMake = null;
    }
  }

  public onMakeFilterChange(value: any) {
    this.makesFiltered = this.makes.filter(
      make => make.name.toLowerCase().indexOf(value.toLowerCase()) !== -1
    );
  }

  public onPartTypeValueChange(item: any) {
    if (item) {
      this.searchModel.filterModel.partType = item['name'];
    } else {
      this.searchModel.filterModel.partType = null;
      this.selectedPartType = null;
    }
  }

  public onPartTypeFilterChange(value: any) {
    this.partTypesFiltered = this.partTypes.filter(
      partType =>
        partType.name.toLowerCase().indexOf(value.toLowerCase()) !== -1
    );
  }

  public onStatusValueChange(item: any) {
    if (item) {
      this.searchModel.filterModel.active = item['name'];
    } else {
      // this is needed if field is "Active" and user clears it, will default back to "Active" instead of blank text
      this.statusCombobox.text = this.statuses[1].name;

      this.searchModel.filterModel.active = this.statuses[1].name;
      this.selectedStatus = this.statuses[1];
    }
  }

  public onAddPart() {
    if (!this.canManageParts) {
      return;
    }
    this.partsService.setPartsSearchInput(this.searchModel);
    this.router.navigate(['./add'], { relativeTo: this.route });
  }

  public onActivateItems() {
    if (!this.canManageParts) {
      return;
    }
    this.dialogService
      .open(ConfirmationComponent, [
        {
          provide: ConfirmationModel,
          useValue: new ConfirmationModel(
            PartsUIMessages.ACTIVATE_PARTS_CONFIRMATION_TITLE,
            PartsUIMessages.ACTIVATE_PARTS_CONFIRMATION
          )
        }
      ])
      .then(confirmed => {
        if (confirmed) {
          this.searchModel.results = [];
          this.loading = true;
          this.progressRef.start();
          this.partsService.activateParts(this.selectedRows).subscribe(
            () => {
              this.loading = false;
              this.progressRef.complete();
              this.toastr.success(PartsUIMessages.ACTIVATE_PARTS_SUCCESS);
              this.onSearch();
            },
            () => {
              this.loading = false;
              this.progressRef.complete();
              this.onAPIError(PartsUIMessages.ACTIVATE_PARTS_ERROR);
            }
          );
        }
      });
  }

  public onRetireItems() {
    if (!this.canManageParts) {
      return;
    }
    this.dialogService
      .open(ConfirmationComponent, [
        {
          provide: ConfirmationModel,
          useValue: new ConfirmationModel(
            PartsUIMessages.RETIRE_PARTS_CONFIRMATION_TITLE,
            PartsUIMessages.RETIRE_PARTS_CONFIRMATION
          )
        }
      ])
      .then(confirmed => {
        if (confirmed) {
          this.searchModel.results = [];
          this.loading = true;
          this.progressRef.start();
          this.partsService.retireParts(this.selectedRows).subscribe(
            () => {
              this.loading = false;
              this.progressRef.complete();
              this.toastr.success(PartsUIMessages.RETIRE_PARTS_SUCCESS);
              this.onSearch();
            },
            () => {
              this.loading = false;
              this.progressRef.complete();
              this.onAPIError(PartsUIMessages.RETIRE_PARTS_ERROR);
            }
          );
        }
      });
  }

  public onRowChecked(selected: any): void {
    this.selectedRows = selected;
  }

  private convertToHtmlContent(labels: Array<string>) {
    let contentAsHtml = '<div class="pull-left">';
    if (labels) {
      labels.sort();
      labels.forEach(label => {
        contentAsHtml = contentAsHtml.concat(
          `<div class="badge sku">${label}</div>`
        );
      });
    }
    contentAsHtml = contentAsHtml.concat('</div>');
    return contentAsHtml;
  }

  public onExportResults(): void {
    if (this.searchModel.totalCount <= 10000) {
      this.dialogService
        .open(ConfirmationComponent, [
          {
            provide: ConfirmationModel,
            useValue: new ConfirmationModel(
              PartsUIMessages.EXPORT_PARTS_CONFIRMATION_TITLE,
              PartsUIMessages.EXPORT_PARTS_CONFIRMATION
            )
          }
        ])
        .then(confirmed => {
          if (confirmed) {
            if (this.authService.isAdmin()) {
              this.onExportResultsAdmin();
            } else {
              this.onExportResultsOther();
            }
          }
        });
    } else {
      this.opened = true;
    }
  }

  public onExportResultsAdmin(): void {
    var grab1KModel = this.searchModel;
    grab1KModel.pageSize = 1000;

    this.partsService.searchParts;
    this.partsService.searchParts(grab1KModel).subscribe((res: any) => {
      let exportResults = res.results;
      this.progressRef.start(); // START PROGRESS
      let jsonData = exportResults.map(row => {
        return {
          Status: row.isActive ? 'Active' : 'InActive',
          'Part Number': row.number,
          'Part Name': row.name,
          Description: row.description,
          'Part Type': row.partType,
          'Product Line': row.productLine,
          'Sub Product Line': row.subProductLine,
          'Caps Min': row.materialCostMinimum,
          'Caps Max': row.materialCostMaximum,
          SKU: row.sku ? row.sku.join(', ') : null,
          Action: row.isActionProduct ? 'Yes' : 'No',
          Manufacturer: row.make,
          'Legacy ID': row.legacyId
        };
      });
      this.exportToExcelService.exportFromJsonToExcel(
        jsonData,
        this.EXCEL_FILE_NAME,
        'xlsx'
      );
      this.onAPISuccess(PartsUIMessages.EXPORT_PARTS_SUCCESS);
    });
  }

  public onExportResultsOther(): void {
    var grab1KModel = this.searchModel;
    grab1KModel.pageSize = 1000;

    this.partsService.searchParts;
    this.partsService.searchParts(grab1KModel).subscribe((res: any) => {
      let exportResults = res.results;
      let jsonData = exportResults.map(row => {
        return {
          Status: row.isActive ? 'Active' : 'InActive',
          'Part Number': row.number,
          'Part Name': row.name,
          Description: row.description,
          'Part Type': row.partType,
          'Product Line': row.productLine,
          'Sub Product Line': row.subProductLine,
          SKU: row.sku ? row.sku.join(', ') : null,
          Action: row.isActionProduct ? 'Yes' : 'No',
          Manufacturer: row.make,
          'Legacy ID': row.legacyId
        };
      });
      this.exportToExcelService.exportFromJsonToExcel(
        jsonData,
        this.EXCEL_FILE_NAME,
        'xlsx'
      );
      this.onAPISuccess(PartsUIMessages.EXPORT_PARTS_SUCCESS);
    });
  }
}
